/**
 * Gateway Logs Tab
 *
 * Displays real-time logs from the Cloud Gateway agent.
 * Auto-refresh with configurable interval and line count.
 */

import { useQuery } from '@tanstack/react-query'
import {
  FileTextIcon,
  Loader2Icon,
  AlertTriangleIcon,
  RefreshCwIcon,
  PauseIcon,
  PlayIcon,
  ArrowDownIcon,
} from 'lucide-react'
import { useState, useEffect, useRef } from 'react'

import { gatewayApi } from '@nebula/shared/services'

const LINE_OPTIONS = [50, 100, 200, 500] as const
const REFRESH_INTERVAL = 5000

type LogLevel = 'ERROR' | 'WARNING' | 'INFO' | 'DEBUG' | 'UNKNOWN'

function parseLogLevel(line: string): LogLevel {
  if (line.includes(' - ERROR - ') || line.includes(' ERROR ')) return 'ERROR'
  if (line.includes(' - WARNING - ') || line.includes(' WARNING ')) return 'WARNING'
  if (line.includes(' - DEBUG - ') || line.includes(' DEBUG ')) return 'DEBUG'
  if (line.includes(' - INFO - ') || line.includes(' INFO ')) return 'INFO'
  return 'UNKNOWN'
}

const levelColors: Record<LogLevel, string> = {
  ERROR: 'text-red-300',
  WARNING: 'text-amber-300',
  INFO: 'text-gray-100',
  DEBUG: 'text-gray-300',
  UNKNOWN: 'text-gray-200',
}

export default function LogsTab() {
  const [lines, setLines] = useState<number>(100)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [autoScroll, setAutoScroll] = useState(true)
  const scrollRef = useRef<HTMLDivElement>(null)

  const {
    data: logsResult,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['gateway-logs', lines],
    queryFn: () => gatewayApi.getGatewayLogs(lines),
    refetchInterval: autoRefresh ? REFRESH_INTERVAL : false,
  })

  const logLines: string[] = logsResult?.result?.logs || []

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [logLines, autoScroll])

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center space-x-2">
          <FileTextIcon className="size-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Logs</h3>
          {logLines.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
              {logLines.length} linhas
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Line count selector */}
          <select
            value={lines}
            onChange={(e) => setLines(Number(e.target.value))}
            className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
          >
            {LINE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} linhas
              </option>
            ))}
          </select>

          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-sm transition-colors ${
              autoRefresh
                ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            title={autoRefresh ? 'Auto-refresh ativo (5s)' : 'Auto-refresh desativado'}
          >
            {autoRefresh ? (
              <PauseIcon className="mr-1.5 size-4" />
            ) : (
              <PlayIcon className="mr-1.5 size-4" />
            )}
            {autoRefresh ? 'Auto' : 'Pausado'}
          </button>

          {/* Scroll to bottom */}
          <button
            onClick={() => {
              setAutoScroll(true)
              if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight
              }
            }}
            className={`inline-flex items-center rounded-lg border px-2 py-1.5 text-sm transition-colors ${
              autoScroll
                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            title="Scroll automático"
          >
            <ArrowDownIcon className="size-4" />
          </button>

          {/* Manual refresh */}
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
          >
            <RefreshCwIcon className={`mr-1.5 size-4 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Log content */}
      {isLoading && logLines.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="size-6 animate-spin text-purple-600" />
        </div>
      ) : logsResult?.error ? (
        <div className="flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertTriangleIcon className="size-5 shrink-0 text-red-500" />
          <span className="text-sm text-red-700">{logsResult.error}</span>
        </div>
      ) : logLines.length === 0 ? (
        <div className="py-12 text-center text-gray-500">
          <FileTextIcon className="mx-auto mb-3 size-10 text-gray-300" />
          <p className="text-sm">Nenhum log disponível.</p>
          <p className="mt-1 text-xs text-gray-400">
            Logs aparecem quando o gateway agent está em execução.
          </p>
        </div>
      ) : (
        <div
          ref={scrollRef}
          onScroll={() => {
            if (!scrollRef.current) return
            const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
            // Disable auto-scroll if user scrolled up
            const atBottom = scrollHeight - scrollTop - clientHeight < 40
            if (!atBottom && autoScroll) setAutoScroll(false)
          }}
          className="max-h-[600px] min-h-[300px] overflow-auto rounded-lg bg-gray-900 p-4 font-mono text-sm leading-6 antialiased"
        >
          {logLines.map((line, i) => {
            const level = parseLogLevel(line)
            return (
              <div key={i} className={`break-all whitespace-pre-wrap ${levelColors[level]}`}>
                {line}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
